import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { AppState, AppSettings, WritingAttempt, GradingResult, WritingTask } from '../types';
import { essayService } from '../services/essayService';

interface AppContextType extends AppState {
  updateSettings: (settings: Partial<AppSettings>) => void;
  addWritingAttempt: (attempt: WritingAttempt) => void;
  setCurrentResult: (result: GradingResult | null) => void;
  setCurrentTask: (task: WritingTask | null) => void;
  setCurrentTaskType: (type: 'task1' | 'task2' | null) => void;
  setCurrentEssay: (essay: string | null) => void;
  setCurrentAttemptId: (attemptId: string | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearHistory: () => void; // Updated to not be async
  deleteAttempt: (id: string) => void; // Updated to not be async
  clearIncompleteEssays: () => Promise<void>;
  setIncompleteEssays: (essays: WritingAttempt[]) => void;
  loadWritingHistory: (userId: string, forceRefresh?: boolean) => Promise<void>;
  refreshWritingHistory: (userId: string) => Promise<void>;
  saveCurrentEssay: () => Promise<void>;
  registerSaveEssayCallback: (callback: () => Promise<void>) => void;
  setOfflineMode: (isOffline: boolean) => void;
}

const defaultSettings: AppSettings = {
  selectedModel: 'gpt-4o-mini',
  testMode: false,
  aiHintsEnabled: true,
};

const sanitizeSettingsForUser = (settings: AppSettings, user: any): AppSettings => {
  if (user?.isTester) {
    return settings;
  }

  return {
    ...settings,
    selectedModel: 'gpt-4o-mini',
    testMode: false,
  };
};


const defaultState: AppState = {
  settings: defaultSettings,
  writingHistory: [], // Start with empty history - no mock data
  incompleteEssays: [], // Start with empty incomplete essays
  currentResult: null,
  currentTask: null,
  currentTaskType: null,
  currentEssay: null,
  currentAttemptId: null,
  isLoading: false,
  error: null,
  isOffline: false,
};

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
  user: any;
  isLoggedIn: boolean;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children, user, isLoggedIn }) => {
  const [state, setState] = useState<AppState>(() => {
    // Load from localStorage
    let saved: string | null = null;
    try {
      saved = localStorage.getItem('ielts-app-state');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to read app state from localStorage:', error);
      }
      return defaultState;
    }
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // If no writing history exists, start with empty array
        if (!parsed.writingHistory || parsed.writingHistory.length === 0) {
          return {
            ...defaultState,
            ...parsed,
            settings: sanitizeSettingsForUser({
              ...defaultSettings,
              ...(parsed.settings || {})
            }, user),
            writingHistory: [],
            // Don't persist loading/error states
            isLoading: false,
            error: null,
          };
        }
        return {
          ...defaultState,
          ...parsed,
          settings: sanitizeSettingsForUser({
            ...defaultSettings,
            ...(parsed.settings || {})
          }, user),
          // Don't persist loading/error states
          isLoading: false,
          error: null,
        };
      } catch (e) {
        console.error('Failed to parse saved state:', e);
        return defaultState;
      }
    }
    return defaultState;
  });

  // Track if data has been loaded from database to avoid redundant fetches
  const [loadedUserId, setLoadedUserId] = React.useState<string | null>(null);

  // Save to localStorage whenever state changes
  useEffect(() => {
    const toSave = {
      settings: state.settings,
      writingHistory: state.writingHistory,
    };
    try {
      localStorage.setItem('ielts-app-state', JSON.stringify(toSave));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to persist app state to localStorage:', error);
      }
    }
  }, [state.settings, state.writingHistory]);


  useEffect(() => {
    if (user?.isTester) return;
    setState(prev => {
      const sanitized = sanitizeSettingsForUser(prev.settings, user);
      if (
        sanitized.selectedModel === prev.settings.selectedModel &&
        sanitized.testMode === prev.settings.testMode &&
        sanitized.aiHintsEnabled === prev.settings.aiHintsEnabled
      ) {
        return prev;
      }
      return {
        ...prev,
        settings: sanitized,
      };
    });
  }, [user]);

  const updateSettings = (settings: Partial<AppSettings>) => {
    setState(prev => ({
      ...prev,
      settings: sanitizeSettingsForUser({ ...prev.settings, ...settings }, user),
    }));
  };

  const addWritingAttempt = async (attempt: WritingAttempt) => {
    // Add to local state immediately for UI responsiveness
    setState(prev => {
      const newHistory = [attempt, ...prev.writingHistory];
      
      // Notifications are now handled by the toast system in individual components
      
      return {
        ...prev,
        writingHistory: newHistory,
      };
    });

    // Save to database in background
    try {
      // Convert WritingAttempt to database format
      const dbAttempt = essayService.convertFromWritingAttempt(attempt, user?.id || '');
      await essayService.saveAttempt(dbAttempt);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save writing attempt to database:', error);
      }
    }
  };

  const setCurrentResult = (result: GradingResult | null) => {
    setState(prev => ({ ...prev, currentResult: result }));
  };

  const setCurrentTask = (task: WritingTask | null) => {
    setState(prev => ({ ...prev, currentTask: task }));
  };

  const setCurrentTaskType = (type: 'task1' | 'task2' | null) => {
    setState(prev => ({ ...prev, currentTaskType: type }));
  };

  const setCurrentEssay = (essay: string | null) => {
    setState(prev => ({ ...prev, currentEssay: essay }));
  };

  const setCurrentAttemptId = (attemptId: string | null) => {
    setState(prev => ({ ...prev, currentAttemptId: attemptId }));
  };

  const setIsLoading = (isLoading: boolean) => {
    setState(prev => ({ ...prev, isLoading }));
  };

  const setError = (error: string | null) => {
    setState(prev => ({ ...prev, error }));
  };

  const clearHistory = () => {
    console.warn('clearHistory called - clearing writingHistory and incompleteEssays');
    setState(prev => ({ ...prev, writingHistory: [], incompleteEssays: [] }));
    
    // Also clear localStorage for incomplete essays
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('essay-draft-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.warn('Cleared localStorage incomplete essays:', keysToRemove.length, 'keys:', keysToRemove);
    } catch (error) {
      console.error('Failed to clear localStorage incomplete essays:', error);
    }
  };

  const deleteAttempt = (id: string) => {
    setState(prev => ({
      ...prev,
      writingHistory: prev.writingHistory.filter(attempt => attempt.id !== id),
    }));
  };

  const clearIncompleteEssays = async () => {
    if (!user?.id) return;
    
    try {
      const success = await essayService.clearIncompleteEssays(user.id);
      if (success) {
        setState(prev => ({ ...prev, incompleteEssays: [] }));
        console.warn('✅ Incomplete essays cleared successfully');
      } else {
        console.error('❌ Failed to clear incomplete essays');
      }
    } catch (error) {
      console.error('Error clearing incomplete essays:', error);
    }
  };

  const setIncompleteEssays = (essays: WritingAttempt[]) => {
    setState(prev => ({ ...prev, incompleteEssays: essays }));
  };

  const withTimeout = React.useCallback(async <T,>(promise: Promise<T>, timeoutMs: number, timeoutCode: string): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        setTimeout(() => reject(new Error(timeoutCode)), timeoutMs);
      }),
    ]);
  }, []);

  const loadWritingHistory = React.useCallback(async (userId: string, forceRefresh: boolean = false) => {
    // Skip if already loaded for this user (unless force refresh)
    if (!forceRefresh && loadedUserId === userId) {
      console.warn('Using cached writing history for user:', userId);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Load both completed and incomplete essays
      const [completedAttempts, incompleteAttempts] = await Promise.all([
        withTimeout(essayService.getAttempts(userId), 12000, 'ATTEMPTS_LOAD_TIMEOUT'),
        withTimeout(essayService.getIncompleteEssays(userId), 12000, 'INCOMPLETE_LOAD_TIMEOUT')
      ]);
      
      // Convert both to WritingAttempt format
      const completedWritingAttempts = completedAttempts.map(attempt => essayService.convertToWritingAttempt(attempt));
      const incompleteWritingAttempts = incompleteAttempts.map(attempt => essayService.convertIncompleteToWritingAttempt(attempt));
      
      // Update state with both completed and incomplete essays from database
      setState(prev => ({
        ...prev,
        writingHistory: completedWritingAttempts,
        incompleteEssays: incompleteWritingAttempts,
        isLoading: false
      }));
      
      // Mark this user's data as loaded
      setLoadedUserId(userId);
    } catch (error) {
      console.error('Failed to load writing history from database:', error);
      // Don't clear existing data on error - preserve what we have in localStorage
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Could not sync latest cloud history. Showing cached data.'
      }));
      // Show a warning but don't fail completely
      if (process.env.NODE_ENV === 'development') {
        console.warn('Using cached data from localStorage due to API error');
      }
    }
  }, [loadedUserId, withTimeout]);

  const refreshWritingHistory = React.useCallback(async (userId: string) => {
    // Force refresh by passing true
    await loadWritingHistory(userId, true);
  }, [loadWritingHistory]);

  // Automatically load writing history when user is authenticated
  useEffect(() => {
    if (user?.id && isLoggedIn) {
      loadWritingHistory(user.id);
    }
  }, [user?.id, isLoggedIn, loadWritingHistory]);

  const [saveEssayCallback, setSaveEssayCallback] = React.useState<(() => Promise<void>) | null>(null);

  const saveCurrentEssay = React.useCallback(async () => {
    if (saveEssayCallback) {
      await saveEssayCallback();
    }
  }, [saveEssayCallback]);

  const registerSaveEssayCallback = React.useCallback((callback: () => Promise<void>) => {
    setSaveEssayCallback(() => callback);
  }, []);

  return (
    <AppContext.Provider
      value={{
        ...state,
        writingHistory: state.writingHistory || [], // Ensure writingHistory is always an array
        updateSettings,
        addWritingAttempt,
        setCurrentResult,
        setCurrentTask,
        setCurrentTaskType,
        setCurrentEssay,
        setCurrentAttemptId,
        setIsLoading,
        setError,
        clearHistory,
        deleteAttempt,
        loadWritingHistory,
        refreshWritingHistory,
        saveCurrentEssay,
        registerSaveEssayCallback,
        clearIncompleteEssays,
        setIncompleteEssays,
        setOfflineMode: (isOffline: boolean) => {
          setState(prev => ({ ...prev, isOffline }));
        },
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
